import React, { useEffect } from 'react';
import { Redirect, Route, Switch, matchPath } from 'react-router-dom';
import ProtectedPage from './components/ProtectedPage';
import Dashboard from './routes/Dashboard';
import Archive from './routes/Archive/Archive';
import Editor from './routes/Editor/Editor';
import useProtectedPageMounted from './hooks/useProtectedPageMounted';
import AccountSettings from './routes/AccountSettings/AccountSettings';
import { HelpCenter } from './routes/HelpCenter/HelpCenter';
import { Chat } from './routes/Chat/Chat';
import { OnboardingDownload } from './routes/OnboardingDownload/OnboardingDownload';
import { useUserContext } from './context/UserContext';
import Folder from './routes/Folder';
import SubtitlesEditor from './routes/SubtitlesEditor/SubtitlesEditor';
import Review from './routes/Review/Review';
import Notifications from './routes/Notifications/Notifications';
import Storyboard from './routes/Storyboard';
import Video from './routes/newVideo/Video';
import { RecordingStudio } from './routes/RecordingStudio/RecordingStudio';
import HomePage from './routes/HomePage/HomePage';
import CompanySettings from './routes/CompanySettings/CompanySettings';
import * as Sentry from '@sentry/react';
import configs from '../../conf';
import { APP_USER_ROLE } from './constants/UserRoles';
import { useLanguageContext } from './context/LanguageContext';
import ExternalEditor from './routes/ExternalEditor/ExternalEditor';
import SchedulePage from './features/Schedule';
import MainContainer from '../userservice/common/component/MainContainer';
import AdvancedEditor from './routes/AdvancedEditor/AdvancedEditor';
import { useSwipeBlocker } from './hooks/useSwipeBlocker';

const config = configs[process.env.APP_CONF];

export default function ProtectedRoutes() {
  const { user, reloadUser, isSentryEnabled, isAppcuesEnabled, userRole, userCompanyId } = useUserContext();
  const { language } = useLanguageContext();

  useEffect(() => {
    const initUser = async () => {
      await reloadUser();
    };
    if (!user) initUser();
  }, [user]);

  useSwipeBlocker();

  useProtectedPageMounted();

  useEffect(() => {
    if (isSentryEnabled && config.sentryDsn) {
      const routes = [
        { path: '/eg/home' },
        { path: '/eg/dashboard' },
        { path: '/eg/archive' },
        { path: '/eg/review' },
        { path: '/eg/edit/:videoId' },
        { path: '/eg/video/:videoId' },
        { path: '/eg/settings' },
        { path: '/eg/company-settings' },
        { path: '/eg/notifications' },
        { path: '/eg/chat' },
        { path: '/eg/help' },
        { path: '/eg/download' },
        { path: '/eg/folder/:folderId' },
        { path: '/eg/subtitles/:videoId' },
        { path: '/eg/storyboard/:videoId' },
        { path: '/eg/recording-studio' },
        { path: '/eg/404' },
      ];

      Sentry.init({
        environment: process.env.NODE_ENV,
        dsn: config.sentryDsn,
        integrations: [
          new Sentry.BrowserTracing({
            routingInstrumentation: Sentry.reactRouterV5Instrumentation(history, routes, matchPath),
          }),
        ],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        tracesSampleRate: 1.0,
      });
    }
  }, [isSentryEnabled]);

  useEffect(() => {
    if (isAppcuesEnabled && userRole) {
      // Include the Appcues script
      const timer = setTimeout(() => {
        const existingScript = document.querySelector(`script[src="//fast.appcues.com/${config.appcuesId}.js"]`);

        // If script is not included, add it
        if (!existingScript) {
          const appcuesSettings = document.createElement('script');
          appcuesSettings.type = 'text/javascript';
          appcuesSettings.innerHTML = 'window.AppcuesSettings = { enableURLDetection: true };';

          // Create the second script element
          const appcues = document.createElement('script');
          appcues.src = `//fast.appcues.com/${config.appcuesId}.js`;
          // Append both scripts to the <head> element
          const head = document.head || document.getElementsByTagName('head')[0];
          head.appendChild(appcuesSettings);
          head.appendChild(appcues);
        }

        setTimeout(() => {
          if (user && userCompanyId) {
            window?.Appcues?.identify(user.username, {
              company_id: userCompanyId,
              role: Object.values(APP_USER_ROLE).find(item => item.TYPE === userRole)?.appcuesTag,
              language: language?.lng,
            });
          }
        }, 500);
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [isAppcuesEnabled, userCompanyId, userRole]);

  return (
    <MainContainer>
      <Switch>
        <Route
          path="/eg/home"
          render={props => (
            <ProtectedPage>
              <HomePage {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/dashboard"
          render={props => (
            <ProtectedPage>
              <Dashboard {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/archive"
          render={props => (
            <ProtectedPage>
              <Archive {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/review"
          render={props => (
            <ProtectedPage>
              <Review {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/edit/:videoId"
          render={props => (
            <ProtectedPage>
              <Editor videoId={props.match.params.videoId} {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/advanced-editor/templates/:templateId"
          render={props => (
            <ProtectedPage>
              <AdvancedEditor key={`template-${props.match.params.templateId}`} templateId={props.match.params.templateId} allowedRoles={[APP_USER_ROLE.SUPER_ADMIN.TYPE]} {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/advanced-editor/:videoId"
          render={props => (
            <ProtectedPage>
              <AdvancedEditor key={`video-${props.match.params.videoId}`} videoId={props.match.params.videoId} {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/edit-external/:videoId"
          render={props => (
            <ProtectedPage>
              <ExternalEditor videoId={props.match.params.videoId} {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/video/:videoId"
          render={props => <Video key={props.match.params.videoId} videoId={props.match.params.videoId} {...props} />}
        />
        <Route
          path="/eg/settings"
          render={props => (
            <ProtectedPage>
              <AccountSettings {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/company-settings"
          render={props => (
            <ProtectedPage>
              <CompanySettings {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/notifications"
          render={props => (
            <ProtectedPage>
              <Notifications {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/chat"
          render={props => (
            <ProtectedPage>
              <Chat {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/help"
          render={props => (
            <ProtectedPage>
              <HelpCenter {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/download"
          render={props => (
            <ProtectedPage>
              <OnboardingDownload {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/folder/:folderId"
          render={props => (
            <ProtectedPage>
              <Folder key={props.match.params.folderId} folderId={props.match.params.folderId} {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/subtitles/:videoId"
          render={props => (
            <ProtectedPage>
              <SubtitlesEditor key={props.match.params.videoId} videoId={props.match.params.videoId} {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/storyboard/:videoId"
          render={props => (
            <ProtectedPage>
              <Storyboard key={props.match.params.videoId} videoId={props.match.params.videoId} {...props} />
            </ProtectedPage>
          )}
        />
        <Route
          path="/eg/recording-studio"
          render={props => (
            <ProtectedPage>
              <RecordingStudio {...props} />
            </ProtectedPage>
          )}
        />

        <Route
          path="/eg/schedule"
          render={props => (
            <ProtectedPage>
              <SchedulePage {...props} />
            </ProtectedPage>
          )}
        />

        <Redirect to="/eg/404" />
      </Switch>
    </MainContainer>
  );
}
